import { cn } from '@heroui/react';
import type { ResizeHandle } from '@/components/canvas/transform';
import { HandleLayer, SelectionBoxLayer } from '@/components/canvas/selection';
import {
  PAGE_FRAME_DRAG_STRIP_SCREEN_PX,
  PAGE_SIZE_LABEL_GAP_SCREEN_PX,
  screenPxToWorld,
  selectionChromeWorldMetrics,
} from '@/components/canvas/selection/selectionChrome';
import { createPagePseudoElement, pageSelectionRect } from '@/components/canvas/selection/pageSelection';
import type { PageResizePreview } from '@/components/canvas/page/controllers/usePageResize';
import { useCanvasMode } from '@/components/canvas/designer/shell/CanvasModeContext';

export interface PageSelectionLayerProps {
  preview: PageResizePreview | null;
  baseSize: { width: number; height: number };
  zoom: number;
  onStartPageResize: (handle: ResizeHandle, e: React.MouseEvent) => void;
  onPageFrameDragStart: (e: React.MouseEvent) => void;
}

/**
 * 页板选中层：复用元素选中的 SelectionBoxLayer + HandleLayer；
 * 仅保留页面专属能力——边框窄条拖拽移动页板、底部尺寸标签。
 */
export function PageSelectionLayer({
  preview,
  baseSize,
  zoom,
  onStartPageResize,
  onPageFrameDragStart,
}: PageSelectionLayerProps) {
  const mode = useCanvasMode();
  const canResizePage = mode !== 'slide';
  const width = preview?.width ?? baseSize.width;
  const height = preview?.height ?? baseSize.height;
  const { inset, pad } = selectionChromeWorldMetrics(zoom);
  const boxW = width + pad;
  const boxH = height + pad;
  const edgeHit = screenPxToWorld(PAGE_FRAME_DRAG_STRIP_SCREEN_PX, zoom);
  const labelGap = screenPxToWorld(PAGE_SIZE_LABEL_GAP_SCREEN_PX, zoom);
  const verticalStripHeight = boxH - edgeHit * 2;
  const verticalStripTop = edgeHit - inset;

  const pseudoElement = createPagePseudoElement(width, height);
  const rect = pageSelectionRect(width, height);
  const selectionBoxStyle = { left: -inset, top: -inset, width: boxW, height: boxH };

  const noop = () => {};

  return (
    <div className="absolute inset-0 z-[6] pointer-events-none">
      <PageFrameDragStrips
        selectionBoxStyle={selectionBoxStyle}
        inset={inset}
        width={width}
        height={height}
        boxW={boxW}
        edgeHit={edgeHit}
        verticalStripTop={verticalStripTop}
        verticalStripHeight={verticalStripHeight}
        onPageFrameDragStart={onPageFrameDragStart}
      />

      <SelectionBoxLayer elements={[pseudoElement]} dragOffset={{ dx: 0, dy: 0 }} zoom={zoom} />

      {canResizePage ? (
        <HandleLayer
          element={pseudoElement}
          rect={rect}
          zoom={zoom}
          isLine={false}
          lineEndX={0}
          lineEndY={0}
          currentRadius={0}
          resizeHandles={['nw', 'ne', 'se', 'sw']}
          showRotate={false}
          showRadiusHandles={false}
          onStartResize={onStartPageResize}
          onStartLineHandle={noop}
          onStartRotate={noop}
          onStartRadiusDrag={noop}
        />
      ) : null}

      <div
        className="pointer-events-none absolute left-1/2 z-[4] -translate-x-1/2"
        style={{ top: height + labelGap }}
      >
        <span
          className={cn(
            'inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium tabular-nums',
            'bg-[var(--accent)] text-[var(--accent-foreground,var(--surface))]',
          )}
        >
          {Math.round(width)} × {Math.round(height)}
        </span>
      </div>
    </div>
  );
}

/** 页面外框四边窄条热区：拖拽移动整页（元素选中组件不包含此交互） */
function PageFrameDragStrips({
  selectionBoxStyle,
  inset,
  width,
  height,
  boxW,
  edgeHit,
  verticalStripTop,
  verticalStripHeight,
  onPageFrameDragStart,
}: {
  selectionBoxStyle: { left: number; top: number; width: number; height: number };
  inset: number;
  width: number;
  height: number;
  boxW: number;
  edgeHit: number;
  verticalStripTop: number;
  verticalStripHeight: number;
  onPageFrameDragStart: (e: React.MouseEvent) => void;
}) {
  return (
    <>
      <div
        data-page-frame-drag
        className="absolute cursor-grab pointer-events-auto"
        style={{ ...selectionBoxStyle, height: edgeHit, zIndex: 10 }}
        onMouseDown={onPageFrameDragStart}
      />
      <div
        data-page-frame-drag
        className="absolute cursor-grab pointer-events-auto"
        style={{
          left: -inset,
          top: height + inset - edgeHit,
          width: boxW,
          height: edgeHit,
          zIndex: 10,
        }}
        onMouseDown={onPageFrameDragStart}
      />
      <div
        data-page-frame-drag
        className="absolute cursor-grab pointer-events-auto"
        style={{
          left: -inset,
          top: verticalStripTop,
          width: edgeHit,
          height: verticalStripHeight,
          zIndex: 1,
        }}
        onMouseDown={onPageFrameDragStart}
      />
      <div
        data-page-frame-drag
        className="absolute cursor-grab pointer-events-auto"
        style={{
          left: width + inset - edgeHit,
          top: verticalStripTop,
          width: edgeHit,
          height: verticalStripHeight,
          zIndex: 10,
        }}
        onMouseDown={onPageFrameDragStart}
      />
    </>
  );
}
